import React from 'react';
import { recoverChunkLoad } from '@utils/version';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@components/ui/button';
import {
  ErrorCategory,
  ErrorSeverity,
  handleError,
} from '@services/errorService';

interface ErrorFallbackProps {
  error: Error | null;
  resetError?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-marble-bg-50 p-6">
    <div className="flex max-w-[500px] flex-col items-center gap-6 text-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-marble-red-100">
        <AlertTriangle
          className="size-10 text-marble-red-600"
          role="img"
          aria-label="error"
        />
      </div>
      <h1 className="text-2xl font-bold tracking-tight text-foreground">
        Something went wrong
      </h1>
      <p className="text-sm text-muted-foreground">
        We apologize for the inconvenience. An unexpected error has occurred.
        Please try refreshing the page or contact support if the problem
        persists.
      </p>
      <div className="flex gap-3">
        <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        {resetError && (
          <Button variant="secondary" onClick={resetError}>
            Try Again
          </Button>
        )}
      </div>
      {process.env.NODE_ENV === 'development' && error && (
        <details className="mt-4 w-full rounded-lg border border-border bg-card p-4 text-left">
          <summary className="cursor-pointer font-semibold">
            Error Details (Development Only)
          </summary>
          <pre className="mt-2 max-h-[200px] overflow-auto rounded bg-marble-bg-50 p-3 text-xs break-words whitespace-pre-wrap">
            {error.message}
          </pre>
          {error.stack && (
            <pre className="mt-2 max-h-[200px] overflow-auto rounded bg-marble-bg-50 p-3 text-xs break-words whitespace-pre-wrap">
              {error.stack}
            </pre>
          )}
        </details>
      )}
    </div>
  </div>
);

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.resetError = this.resetError.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    void recoverChunkLoad(error).then((outdated) => {
      if (outdated) window.location.reload();
    });
    // Use centralized error service
    handleError(error, {
      category: ErrorCategory.RENDER,
      severity: ErrorSeverity.CRITICAL,
      silent: true, // Don't show toast, we have our own UI
      extra: {
        componentStack: errorInfo?.componentStack,
      },
    });
  }

  resetError(): void {
    this.setState({ hasError: false, error: null });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Allow custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback error={this.state.error} resetError={this.resetError} />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
